import styled from '@emotion/styled';
import { typographyFontSizeCfg } from 'ds/components/typography/types';

export const CardWrapper = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.colors.neutrals.white};
  overflow: hidden;

  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: ${({ theme }) => theme.spacing(1)};

  ${({ theme }) => theme.media.showAt({ from: 3 })`
    border-radius: ${theme.borderRadius.small};
    padding: 0;
  `}
`;

export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.25)};

  ${({ theme }) => theme.media.showAt({ from: 3 })`
    flex-direction: row;
    gap: 0;
  `}
`;

export const ImageSection = styled.div`
  position: relative;
  flex-shrink: 0;
  align-self: flex-start;

  width: 100%;
  height: 236px;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.small};

  > div,
  > div > div {
    width: 100%;
    height: 100%;
  }

  > div > div > div:last-child {
    width: 100% !important;
    min-width: unset !important;
    height: 100% !important;
    min-height: unset !important;
    border: none;
    border-radius: 0;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ${({ theme }) => theme.media.showAt({ from: 3 })`
    width: 198px;
    height: 210px;
    border-radius: ${theme.borderRadius.tiny};
    padding: 3px;

    > div > div > div:last-child {
      border-radius: ${theme.borderRadius.tiny};
    }
  `}
`;

export const FavoriteWrapper = styled.div`
  position: absolute;
  z-index: 2;
  left: ${({ theme }) => theme.spacing(1)};
  top: ${({ theme }) => theme.spacing(1)};

  ${({ theme }) => theme.media.showAt({ at: 1 })`
    left: ${theme.spacing(2)};
    top: ${theme.spacing(2)};
  `}
`;

export const ContentSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  min-width: 0;

  ${({ theme }) => theme.media.showAt({ from: 3 })`
    padding: ${theme.spacing(1.5)} ${theme.spacing(3)};
  `}
`;

export const CardTitle = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutrals.grey1};
  font-size: 17px;
  line-height: 24px;
  text-align: right;

  ${({ theme }) => theme.media.showAt({ from: 3 })`
    font-size: 15px;
  `}
`;

export const IndicatorRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

export const CompareWrapper = styled.div`
  position: absolute;
  z-index: 2;
  right: ${({ theme }) => theme.spacing(1)};
  top: ${({ theme }) => theme.spacing(1)};
`;

export const AttributesRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};

  ${({ theme }) => theme.media.showAt({ from: 3 })`
    flex-wrap: nowrap;
    overflow: hidden;
  `}
`;

export const AttributeText = styled.span`
  font-size: 13px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.neutrals.grey1};
  white-space: nowrap;
`;

export const AttributeSeparator = styled.span`
  display: inline-block;
  width: 1px;
  height: 20px;
  background-color: ${({ theme }) => theme.colors.neutrals.grey6};
  flex-shrink: 0;
  align-self: center;
`;

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(0.5)};

  ${({ theme }) => theme.media.showAt({ from: 3 })`
    grid-template-columns: 1fr 1fr 1fr;
    gap: ${theme.spacing(1)};
  `}
`;

export const MetricBox = styled.div<{ fullWidthOnMobile?: boolean }>`
  background-color: ${({ theme }) => theme.colors.neutrals.grey10};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;

  ${({ fullWidthOnMobile, theme }) =>
    fullWidthOnMobile &&
    theme.media.showAt({ at: 1 })`
      grid-column: 1 / -1;
    `}

  ${({ theme }) => theme.media.showAt({ from: 3 })`
    padding: ${theme.spacing(1.5)} ${theme.spacing(2)};
  `}
`;

export const MetricLabel = styled.span`
  font-size: 13px;
  line-height: 15px;
  color: ${({ theme }) => theme.colors.neutrals.grey1};
  max-width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const MetricValue = styled.span<{ bigText?: boolean; bigLine?: boolean; direction?: 'ltr' | 'rtl' }>`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutrals.grey1};

  font-size: ${({ bigText }) => bigText ? '21px' : '15px'};
  line-height: ${({ bigLine }) => bigLine ? '32px' : '25px'};
  overflow-wrap: break-word;
  word-break: break-word;
  ${({ direction }) => direction ? `direction: ${direction};` : ''}

  ${({ theme }) => theme.media.showAt({ from: 3 })`
    font-size: 21px;
  `}
`;

export const PriceDropped = styled.div`
  text-decoration: line-through;
  ${({ theme }) => `
    color: ${theme.colors.neutrals.grey1};
    font-size: ${typographyFontSizeCfg.smallText}px;
    line-height: 15px;
  `}
  ${({ theme }) => theme.media.showAt({ to: 2 })`
    color: ${theme.colors.neutrals.grey2};
    line-height: 25px;
  `}
`;

export const PriceWrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  gap: ${({ theme }) => theme.spacing(1)};
  align-items: flex-end;
`;
